import { Fragment } from 'react';
import Head from 'next/head';
import slugify from '@sindresorhus/slugify';
import { jsonLdScriptProps } from 'react-schemaorg';

import Layout from '@/components/Layout/Vanilla';
import Hero from '@/components/Hero';
import Section from '@/components/Section';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import Heading from '@/components/renderers/Heading';
import TOC from '@/components/TOC';
import Button from '@/components/Button';
import Arrow from '@/svg/icons/arrow.svg';
import { fetchAPI, gql } from '@/lib/graphcms';
import { serialize } from '@/utils/serializeJsonLd';

export default function FAQCategory({
  preview,
  faqCategory: { faqs, ...page },
}) {
  const headings = faqs.map(({ question }) => question);

  const mainEntity =
    faqs.length > 0 &&
    faqs.map(({ question: name, answer }) => {
      const mdRegexp = /\[|\]\(.*\)|[#*]/gi;
      const text = serialize(answer.replace(mdRegexp, ''));

      return {
        '@type': 'Question',
        name,
        acceptedAnswer: {
          '@type': 'Answer',
          text,
        },
      };
    });

  return (
    <Layout seo={page} preview={preview}>
      <Head>
        <script
          {...jsonLdScriptProps({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity,
          })}
        />
      </Head>

      <Section size="small">
        <Hero {...page} />
      </Section>

      <div className="container mx-auto pt-0 md:flex md:flex-row md:overflow-visible">
        <aside
          className="px-8 md:sticky md:mt-10 md:mb-6 md:max-h-3/4 md:w-1/3 md:overflow-y-auto md:pr-0 lg:order-first lg:ml-10 lg:w-1/4"
          style={{ top: '160px' }}
        >
          <TOC
            textSizes="xl:text-sm md:text-sm"
            links={headings}
            heading="In this category:"
          />
          <Button
            block
            className="flex flex-row justify-center"
            colour="purple"
            href="/faq"
          >
            <Arrow
              title="Leftwards arrow"
              className="mr-3 inline h-6 fill-current"
            />
            View all FAQs
          </Button>
        </aside>

        <main className="mb-16 px-8 md:w-2/3 lg:order-last lg:w-3/4 lg:px-12">
          {faqs.map(({ id, question, answer }) => (
            <Fragment key={id}>
              <Heading
                id={slugify(question, {
                  customReplacements: [
                    ['/', ''],
                    ['.', ''],
                  ],
                  decamelize: false,
                })}
                level={2}
              >
                {question}
              </Heading>
              <MarkdownRenderer content={answer} />
            </Fragment>
          ))}
        </main>
      </div>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false, params }) => {
  const { faqCategory } =
    (await fetchAPI(
      gql`
        fragment seoData on Seo {
          changefreq
          description
          image {
            url
            width
            height
            mimeType
          }
          keywords
          noindex
          priority
          title
        }

        query faqPageQuery($slug: String!) {
          faqCategory(where: { slug: $slug }) {
            id
            title
            slug
            faqs {
              answer
              id
              question
            }
            meta: seo {
              ...seoData
            }
          }
        }
      `,
      {
        preview,
        variables: {
          slug: params.slug,
        },
      },
    )) || [];

  if (!faqCategory) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      preview,
      faqCategory,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths = async () =>
  Promise.resolve({
    paths: [],
    fallback: `blocking`,
  });
